/**
 * Test page for City/State Component
 * This tests the new React Select city/state implementation
 */

import React, { useState } from 'react';
import Select from 'react-select';
import { Country, State, City } from 'country-state-city';

interface CityStateTestProps {}

export const CityStateTest: React.FC<CityStateTestProps> = () => {
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');

  // Get US states for dropdown
  const usStates = State.getStatesOfCountry('US').map((state) => ({
    value: state.isoCode,
    label: state.name,
  }));

  // Get cities based on selected state
  const getCitiesForState = (stateCode: string) => {
    if (!stateCode) return [];
    return City.getCitiesOfState('US', stateCode).map((city) => ({
      value: city.name,
      label: city.name,
    }));
  };

  const cityOptions = selectedState ? getCitiesForState(selectedState) : [];

  // Custom styles for React Select to match our form styling
  const selectStyles = {
    control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : undefined,
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#94a3b8',
      },
      minHeight: '42px',
      fontSize: '14px',
    }),
    option: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
      ...provided,
      fontSize: '14px',
      backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
      color: '#1e293b',
    }),
    placeholder: (provided: Record<string, unknown>) => ({
      ...provided,
      color: '#64748b',
      fontSize: '14px',
    }),
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-xs p-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-6">City/State Component Test</h1>

          <div className="space-y-6">
            {/* State Dropdown */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">State *</label>
              <Select
                value={usStates.find((state) => state.value === selectedState) || null}
                onChange={(selectedOption) => {
                  const stateCode = selectedOption?.value || '';
                  setSelectedState(stateCode);
                  // Reset city when state changes
                  if (stateCode !== selectedState) {
                    setSelectedCity('');
                  }
                }}
                options={usStates}
                styles={selectStyles}
                placeholder="Select state..."
                isSearchable={true}
                isClearable={true}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* City Autocomplete */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">City *</label>
              <Select
                value={cityOptions.find((city) => city.value === selectedCity) || null}
                onChange={(selectedOption) => {
                  setSelectedCity(selectedOption?.value || '');
                }}
                options={cityOptions}
                styles={selectStyles}
                placeholder={selectedState ? 'Select city...' : 'Select state first...'}
                isSearchable={true}
                isClearable={true}
                isDisabled={!selectedState}
                className="react-select-container"
                classNamePrefix="react-select"
                noOptionsMessage={() => (selectedState ? 'No cities found' : 'Select state first')}
              />
            </div>

            {/* Results Display */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Selected Values:</h3>
              <div className="space-y-1 text-sm text-slate-600">
                <div>
                  <strong>State:</strong> {selectedState || 'None selected'}
                </div>
                <div>
                  <strong>City:</strong> {selectedCity || 'None selected'}
                </div>
                <div>
                  <strong>Combined:</strong>{' '}
                  {selectedCity && selectedState
                    ? `${selectedCity}, ${selectedState}`
                    : 'Incomplete selection'}
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Data Statistics:</h3>
              <div className="space-y-1 text-sm text-blue-700">
                <div>
                  <strong>Total US States/Territories:</strong> {usStates.length}
                </div>
                <div>
                  <strong>Cities in Selected State:</strong> {cityOptions.length}
                </div>
                {selectedState && (
                  <div>
                    <strong>Selected State Name:</strong>{' '}
                    {usStates.find((s) => s.value === selectedState)?.label || 'Unknown'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityStateTest;
